import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
  Tabs,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { itemPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { catalogApi, itemStatusMeta, ITEM_STATUSES } from './catalogApi';
import { settingsApi } from '../settings/settingsApi';
import ItemForm from './ItemForm';
import CategoriesTab from './CategoriesTab';
import UnitsTab from './UnitsTab';
import PriceMovementsTab from './PriceMovementsTab';
import './catalog.css';

const PAGE_SIZE = 25;

const TABS = [
  { id: 'items', label: 'Items' },
  { id: 'movements', label: 'Price movements' },
  { id: 'categories', label: 'Categories' },
  { id: 'units', label: 'Units' },
];


export default function CatalogPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();
  const { hasPermission, activePropertyId } = useProperty();
  const canManage = hasPermission('catalog.manage');

  const [tab, setTab] = useState('items');

  // Lookups shared across tabs
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // Items list state
  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [page, setPage] = useState(1);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const debounceRef = useRef(null);

  const loadLookups = useCallback(async () => {
    setLookupsLoading(true);
    try {
      const [cats, us, eff] = await Promise.all([
        catalogApi.listCategories(),
        catalogApi.listUnits(),
        settingsApi.getEffective(activePropertyId).catch(() => null),
      ]);
      setCategories(cats || []);
      setUnits(us || []);
      setCostCenters(eff?.costCenters || []);
      setGlAccounts(eff?.glAccounts || []);
    } catch {
      // Non-fatal; items still load.
    } finally {
      setLookupsLoading(false);
    }
  }, [activePropertyId]);

  const filterFields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: ITEM_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'categoryIds', label: t('Category'), type: 'multiselect', searchable: true, options: categories.map((c) => ({ value: c.id, label: c.name })) },
    { id: 'uomIds', label: t('Stock unit'), type: 'multiselect', searchable: true, options: units.map((u) => ({ value: u.id, label: u.code || u.name })) },
    { id: 'cost', label: t('Std cost'), type: 'numberrange', minParam: 'costMin', maxParam: 'costMax' },
  ], [categories, units, t]);

  const filterParams = useMemo(() => buildFilterParams(filterFields, values), [filterFields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await catalogApi.listItems({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load items.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const catName = (id) => categories.find((c) => c.id === id)?.name;
  const uomCode = (id) => units.find((u) => u.id === id)?.code;

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const openItem = (item) => navigate(itemPath(item));

  const handleCreate = async (payload) => {
    setSubmitting(true);
    try {
      const created = await catalogApi.createItem(payload);
      toast.success(t('Item created'));
      setDrawerOpen(false);
      if (created?.id) navigate(itemPath(created));
      else await loadItems();
    } catch (err) {
      toast.error(t('Could not create item'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogApi.removeItem(deleteTarget.id);
      toast.success(t('Item deleted'));
      setDeleteTarget(null);
      if (data.items.length === 1 && page > 1) setPage((p) => p - 1);
      else await loadItems();
    } catch (err) {
      toast.error(t('Could not delete item'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('Item'),
      width: 280,
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          {row.sku && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{row.sku}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('Status'),
      width: 110,
      render: (row) => {
        const m = itemStatusMeta(row.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    {
      key: 'category',
      header: t('Category'),
      width: 180,
      render: (row) => catName(row.categoryId) || <span className="pp-cell-muted">—</span>,
    },
    {
      key: 'uom',
      header: t('Stock unit'),
      width: 120,
      render: (row) => uomCode(row.stockUomId) || <span className="pp-cell-muted">—</span>,
    },
    {
      key: 'cost',
      header: t('Std cost'),
      width: 120,
      align: 'right',
      render: (row) =>
        row.standardCost ? money(row.standardCost) : <span className="pp-cell-muted">—</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 90,
      align: 'right',
      resizable: false,
      render: (row) => (
        <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton aria-label={t('Open item')} variant="ghost" size="icon-sm" onClick={() => openItem(row)}>
            <Icon.ArrowRight size={15} />
          </IconButton>
          {canManage && (
            <IconButton aria-label={t('Delete item')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(row)}>
              <Icon.Trash size={15} />
            </IconButton>
          )}
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Catalog')}
        description={t('Your item master — products, units, categories and supplier pricing.')}
        icon={<Icon.Package size={22} />}
        tabs={<Tabs tabs={TABS.map((x) => ({ ...x, label: t(x.label) }))} value={tab} onChange={setTab} ariaLabel={t('Catalog sections')} />}
        actions={
          tab === 'items' && canManage ? (
            <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => setDrawerOpen(true)}>
              {t('New item')}
            </Button>
          ) : null
        }
      />

      {tab === 'items' && (
        <>
          <FilterBar
            searchPlaceholder={t('Search by name, SKU or barcode…')}
            searchValue={searchText}
            onSearchChange={onSearchChange}
            fields={filterFields}
            values={values}
            onChange={onFilterChange}
          />

          {error ? (
            <EmptyState
              icon={<Icon.CircleAlert size={28} />}
              title={t("Couldn't load items")}
              description={error}
              action={<Button variant="secondary" onClick={loadItems}>{t('Retry')}</Button>}
            />
          ) : loading ? (
            <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}>
              <Spinner size={26} />
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                data={data.items}
                rowKey={(row) => row.id}
                density="comfortable"
                onRowClick={openItem}
                emptyState={
                  <EmptyState
                    icon={<Icon.Package size={28} />}
                    title={hasFilters ? t('No matching items') : t('No items yet')}
                    description={
                      hasFilters
                        ? t('Try adjusting your search or filters.')
                        : t('Add your first item to start building the catalog.')
                    }
                    action={
                      !hasFilters && canManage ? (
                        <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => setDrawerOpen(true)}>
                          {t('New item')}
                        </Button>
                      ) : null
                    }
                  />
                }
              />
              {data.totalCount > 0 && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'movements' && (
        <PriceMovementsTab categories={categories} canManage={canManage} />
      )}

      {tab === 'categories' && (
        <CategoriesTab categories={categories} loading={lookupsLoading} canManage={canManage} onChanged={loadLookups} />
      )}

      {tab === 'units' && (
        <UnitsTab units={units} loading={lookupsLoading} canManage={canManage} onChanged={loadLookups} />
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => (submitting ? null : setDrawerOpen(false))}
        title={t('New item')}
        description={t('Add a product to your catalog. You can complete suppliers and par levels next.')}
        size="md"
      >
        <ItemForm
          item={null}
          categories={categories}
          units={units}
          costCenters={costCenters}
          glAccounts={glAccounts}
          submitting={submitting}
          onSubmit={handleCreate}
          onCancel={() => setDrawerOpen(false)}
        />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('Delete item')}
        description={deleteTarget ? t('Delete "{name}"? This also removes its supplier price-list links. This cannot be undone.', { name: deleteTarget.name }) : ''}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
      />
    </PageContainer>
  );
}
